'use client'

// My-listings dashboard card — prototype mylistings.jsx (ML_STATUS, MlStat,
// SuggestionRow, LiveUpdates, MyListingCard). Local demo store only.
import * as React from 'react'
import { useRouter } from 'next/navigation'
import type { LucideIcon } from 'lucide-react'
import {
  CirclePause,
  CirclePlay,
  Eye,
  Handshake,
  Heart,
  Megaphone,
  MoreHorizontal,
  PenLine,
  Plus,
  Radio,
  Rocket,
  Tag,
  Trash2,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Chip, IconBtn, PhotoBlock, RMenu, fmtCZK, pillBtnBase, useRToast } from '@/components/roostr'
import { MoneyInput } from './form-bits'
import type { RMenuItem } from '@/components/roostr'
import { useI18n } from '@/lib/i18n'
import { MIcon } from './icons'
import { ListingPreviewModal } from './listing-preview-modal'
import { isPromoActive, PromoteModal } from './promote-modal'
import { useMyListingsStore } from './my-listings-store'
import type { MyListing, MyListingStatus, MyListingSuggestion, MyListingUpdate } from './post-data'

// The prototype's `.rcard` hover treatment (theme.css:135-141), ported the
// same way listing-card.tsx / saved-search-card.tsx do it.
const rcardHover =
  'transition-[box-shadow,transform,border-color] duration-200 ease-standard hover:-translate-y-0.5 hover:shadow-e2'

// ML_STATUS — lucide equivalents of the prototype's material icons
// (live: sensors → Radio; paused: pause_circle → CirclePause;
//  draft: edit_note → PenLine; rented: handshake → Handshake).
const ML_STATUS: Record<MyListingStatus, { label: string; cls: string; icon: LucideIcon }> = {
  live: { label: 'Live', cls: 'text-success', icon: Radio },
  paused: { label: 'Paused', cls: 'text-warning', icon: CirclePause },
  draft: { label: 'Draft', cls: 'text-ink-muted', icon: PenLine },
  rented: { label: 'Rented', cls: 'text-info', icon: Handshake },
}

function MlStat({
  icon: Icon,
  value,
  label,
  dim,
}: {
  icon: LucideIcon
  value: React.ReactNode
  label: React.ReactNode
  dim?: boolean
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Icon size={16} className="text-ink-muted" />
      <div className="min-w-0">
        <div className="flex items-center gap-[6px]">
          <span className={cn('font-mono text-[14.5px] font-bold', dim ? 'text-ink-muted' : 'text-ink')}>{value}</span>
        </div>
        <div className="text-[10.5px] text-ink-muted">{label}</div>
      </div>
    </div>
  )
}

// Asking price stat — clicking it opens the full price editor below (user
// request 2026-07-15: every price component editable from the dashboard).
function AskingPriceStat({
  l,
  label,
  dim,
  onToggle,
  open,
}: {
  l: MyListing
  label: string
  dim?: boolean
  onToggle: () => void
  open: boolean
}) {
  const { t } = useI18n()
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Tag size={16} className="text-ink-muted" />
      <div className="min-w-0">
        <button
          type="button"
          onClick={onToggle}
          title={t('myListings.editPrices', 'Edit rent, utilities, deposit and fees')}
          className={cn(
            'group/price flex items-center gap-[5px] font-mono text-[14.5px] font-bold',
            dim ? 'text-ink-muted' : 'text-ink'
          )}
        >
          {fmtCZK(l.price)}
          <PenLine
            size={11}
            className={cn(
              'shrink-0 text-ink-muted transition-opacity',
              open ? 'opacity-100 text-primary' : 'opacity-0 group-hover/price:opacity-100'
            )}
          />
        </button>
        <div className="text-[10.5px] text-ink-muted">{label}</div>
      </div>
    </div>
  )
}

// The composer's Price section, transplanted onto the dashboard card: rent,
// utilities, deposit and extra fees all edit in place. Writes hit the listing
// AND its draft snapshot, so search card + detail preview stay in lockstep.
function PriceEditor({ l, onClose }: { l: MyListing; onClose: () => void }) {
  const { t } = useI18n()
  const updateMyListing = useMyListingsStore((s) => s.updateMyListing)
  const utilities = l.utilities ?? l.draft?.utilities ?? 0
  const deposit = l.deposit ?? l.draft?.deposit ?? 0
  const extras = l.extras ?? l.draft?.extras ?? []
  const patch = (p: Partial<Pick<MyListing, 'price' | 'utilities' | 'deposit' | 'extras'>>) => {
    const next: Partial<MyListing> = { ...p }
    if (l.draft) {
      next.draft = {
        ...l.draft,
        price: p.price ?? l.price,
        utilities: p.utilities ?? utilities,
        deposit: p.deposit ?? deposit,
        extras: p.extras ?? extras,
      }
    }
    updateMyListing(l.id, next)
  }
  const addExtra = (label: string, amount: number, custom: boolean) =>
    patch({ extras: [...extras, { id: 'x' + Date.now() + extras.length, label, amount, custom }] })
  const extrasMonthly = extras.reduce((a, e) => a + (e.amount || 0), 0)
  return (
    <div className="animate-card-in rounded-shape-sm bg-surface-low px-3 py-[10px]">
      <div className="mb-2 flex items-center gap-[6px]">
        <Tag size={15} className="text-primary" />
        <span className="font-mono text-[10px] font-bold tracking-[0.6px] text-ink-muted">
          {t('myListings.pricesLabel', 'PRICES')}
        </span>
        <span className="text-[10.5px] text-ink-muted">
          · {t('myListings.pricesHint', 'changes go live immediately')}
        </span>
        <button type="button" onClick={onClose} className="ml-auto text-[10.5px] font-semibold text-ink-muted hover:text-ink">
          {t('listingDetail.hideSummary', 'Hide')}
        </button>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-[10px]">
          <span className="flex-1 text-[12.5px] text-ink-secondary">
            {t('post.monthlyRent', 'Monthly rent')}{' '}
            <span className="text-ink-muted">{t('post.perMonthLabel', '/ month')}</span>
          </span>
          <MoneyInput value={l.price} onChange={(v) => patch({ price: v })} aria-label={t('post.monthlyRent', 'Monthly rent')} />
        </div>
        <div className="flex items-center gap-[10px]">
          <span className="flex-1 text-[12.5px] text-ink-secondary">
            {t('post.utilities', 'Utilities')} <span className="text-ink-muted">{t('post.perMonthLabel', '/ month')}</span>
          </span>
          <MoneyInput value={utilities} onChange={(v) => patch({ utilities: v })} aria-label={t('post.utilities', 'Utilities')} />
        </div>
        <div className="flex items-center gap-[10px]">
          <span className="flex-1 text-[12.5px] text-ink-secondary">
            {t('post.deposit', 'Deposit')} <span className="text-ink-muted">{t('post.oneTime', '/ one-time')}</span>
          </span>
          <MoneyInput value={deposit} onChange={(v) => patch({ deposit: v })} aria-label={t('post.deposit', 'Deposit')} />
        </div>
        {extras.map((e) => (
          <div key={e.id} className="flex animate-pop-in items-center gap-[10px] [animation-duration:0.15s]">
            {e.custom ? (
              <input
                value={e.label}
                onChange={(ev) =>
                  patch({ extras: extras.map((x) => (x.id === e.id ? { ...x, label: ev.target.value } : x)) })
                }
                placeholder={t('post.customFeePlaceholder', 'What’s it for?')}
                className="min-w-0 flex-1 border-b border-dashed border-outline bg-transparent py-[2px] text-[12.5px] text-ink outline-none placeholder:text-ink-muted"
              />
            ) : (
              <span className="flex-1 text-[12.5px] text-ink-secondary">
                {e.label} <span className="text-ink-muted">{t('post.perMonthLabel', '/ month')}</span>
              </span>
            )}
            <MoneyInput
              value={e.amount}
              onChange={(v) => patch({ extras: extras.map((x) => (x.id === e.id ? { ...x, amount: v } : x)) })}
            />
            <IconBtn
              size={26}
              title={t('post.remove', 'Remove')}
              onClick={() => patch({ extras: extras.filter((x) => x.id !== e.id) })}
            >
              <X size={14} className="text-ink-muted" />
            </IconBtn>
          </div>
        ))}
      </div>
      <div className="mt-[10px] flex flex-wrap gap-[6px]">
        {(
          [
            [t('post.extraParking', 'Parking'), 1200],
            [t('post.extraInternet', 'Internet'), 450],
            [t('post.extraCleaning', 'Cleaning'), 800],
          ] as Array<[string, number]>
        )
          .filter(([label]) => !extras.some((e) => e.label === label))
          .map(([label, amount]) => (
            <Chip
              key={label}
              small
              dashed
              icon={<Plus size={13} />}
              label={label}
              onClick={() => addExtra(label, amount, false)}
              title={`${t('post.addFee', 'Add')} ${label.toLocaleLowerCase()}`}
            />
          ))}
        <Chip
          small
          dashed
          icon={<Plus size={13} />}
          label={t('post.extraCustom', 'Custom…')}
          onClick={() => addExtra('', 0, true)}
          title={t('post.addCustomFee', 'Add your own fee')}
        />
      </div>
      <div className="mt-2.5 border-t border-outline-variant pt-2 text-[12px] text-ink-secondary">
        {t('post.tenantPays', 'Tenant pays')} <b className="font-mono">{fmtCZK(l.price + utilities + extrasMonthly)}</b>
        {t('post.allInSuffix', '/mo all-in, plus')} <span className="font-mono">{fmtCZK(deposit)}</span>{' '}
        {t('post.depositOnce', 'deposit once.')}
      </div>
    </div>
  )
}

function SuggestionRow({
  s,
  onAction,
  onDismiss,
}: {
  s: MyListingSuggestion
  onAction: () => void
  onDismiss: () => void
}) {
  const { t } = useI18n()
  return (
    <div className="flex animate-card-in items-start gap-[10px] rounded-shape-sm bg-surface-low px-3 py-[10px]">
      <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-primary-container">
        <MIcon name={s.icon} size={14} className="text-on-primary-container" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] leading-[1.5] text-ink-secondary">{s.text}</div>
        <div className="mt-[7px] flex items-center gap-[10px]">
          <button
            type="button"
            className={cn(pillBtnBase, 'bg-primary-container px-[13px] py-[6px] text-[12px] text-on-primary-container')}
            onClick={onAction}
          >
            {s.action}
          </button>
          <button type="button" className="text-[11.5px] text-ink-muted" onClick={onDismiss}>
            {t('myListings.dismiss', 'Dismiss')}
          </button>
        </div>
      </div>
    </div>
  )
}

// Live update — ONE timestamped owner note pinned to the listing (user
// decision 2026-07-15: single active update, tweet-length). The description
// itself stays untouched.
const UPDATE_MAX = 144

function LiveUpdates({ l, onChange }: { l: MyListing; onChange: (ups: MyListingUpdate[]) => void }) {
  const { t } = useI18n()
  const { show } = useRToast()
  const [v, setV] = React.useState('')
  const ups = l.updates || []
  const hasUpdate = ups.length > 0
  const post = () => {
    if (v.trim().length < 3 || hasUpdate) return
    onChange([{ id: 'u' + Date.now(), text: v.trim().slice(0, UPDATE_MAX), when: 'just now' }])
    setV('')
    show({ msg: t('myListings.updatePosted', 'Live update posted — pinned to the top of your listing') })
  }
  const canPost = v.trim().length >= 3
  return (
    <div className="rounded-shape-sm bg-surface-low px-3 py-[10px]">
      <div className={cn('flex items-center gap-[6px]', hasUpdate ? 'mb-2' : 'mb-[6px]')}>
        <Megaphone size={15} className="text-primary" />
        <span className="font-mono text-[10px] font-bold tracking-[0.6px] text-ink-muted">
          {t('myListings.liveUpdate', 'LIVE UPDATE')}
        </span>
        <span className="text-[10.5px] text-ink-muted">
          · {t('myListings.liveUpdateHint', 'one short note pinned on the listing')}
        </span>
      </div>
      {ups.map((u) => (
        <div key={u.id} className="flex animate-card-in items-start gap-2 py-[5px] [animation-duration:0.2s]">
          <span className="mt-[6px] h-[6px] w-[6px] shrink-0 rounded-full bg-primary" />
          <span className="flex-1 text-[12.5px] leading-[1.45] text-ink">{u.text}</span>
          <span className="mt-[2px] whitespace-nowrap font-mono text-[10px] text-ink-muted">{u.when}</span>
          <IconBtn size={22} title={t('myListings.removeUpdate', 'Remove update')} onClick={() => onChange([])}>
            <X size={13} className="text-ink-muted" />
          </IconBtn>
        </div>
      ))}
      {hasUpdate ? (
        <div className="mt-[2px] text-[10.5px] text-ink-muted">
          {t('myListings.oneUpdateNote', 'One update at a time — remove this one to post another.')}
        </div>
      ) : (
        <div className="mt-0 flex gap-2">
          <div className="relative min-w-0 flex-1">
            <input
              value={v}
              maxLength={UPDATE_MAX}
              onChange={(e) => setV(e.target.value.slice(0, UPDATE_MAX))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') post()
              }}
              placeholder={t('myListings.updatePlaceholder', 'e.g. Viewings this Saturday · Available from Sept 1 …')}
              className="w-full rounded-full border border-outline bg-surface py-[7px] pl-[13px] pr-[52px] text-[12.5px] text-ink outline-none placeholder:text-ink-muted"
            />
            <span
              className={cn(
                'pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 font-mono text-[10px]',
                v.length >= UPDATE_MAX ? 'font-bold text-warning' : 'text-ink-muted'
              )}
            >
              {v.length}/{UPDATE_MAX}
            </span>
          </div>
          <button
            type="button"
            onClick={post}
            disabled={!canPost}
            className={cn(
              pillBtnBase,
              'shrink-0 px-[15px] py-[7px] text-[12.5px]',
              canPost ? 'cursor-pointer bg-primary-container text-on-primary-container' : 'cursor-default bg-surface-high text-ink-muted'
            )}
          >
            {t('myListings.post', 'Post')}
          </button>
        </div>
      )}
    </div>
  )
}

export function MyListingCard({ l }: { l: MyListing }) {
  const { t } = useI18n()
  const { show } = useRToast()
  const router = useRouter()
  const { updateMyListing, deleteMyListing, dismissSuggestion, restoreListing } = useMyListingsStore()
  const [previewOpen, setPreviewOpen] = React.useState(false)
  const [promoteOpen, setPromoteOpen] = React.useState(false)
  const [pricesOpen, setPricesOpen] = React.useState(false)
  // Time-boxed: the paid week lapsing flips this off with no action needed.
  const promoted = isPromoActive(l)
  const st = ML_STATUS[l.status]
  const StIcon = st.icon

  const statusLabel = {
    live: t('myListings.statusLive', 'Live'),
    paused: t('myListings.statusPaused', 'Paused'),
    draft: t('myListings.statusDraft', 'Draft'),
    rented: t('myListings.statusRented', 'Rented'),
  }[l.status]

  const applySuggestion = (s: MyListingSuggestion) => {
    if (s.goPost) {
      router.push('/post')
      return
    }
    if (s.apply) {
      updateMyListing(l.id, s.apply)
      show({
        msg: s.apply.price
          ? `${t('myListings.priceUpdatedTo', 'Price updated to')} ${fmtCZK(s.apply.price)}`
          : t('myListings.resumedToast', 'Listing resumed — back in search results'),
      })
    } else {
      // The freshly-published share suggestion — demo copy, no real link.
      show({ msg: t('myListings.shareCopied', 'Share link copied') })
    }
    dismissSuggestion(l.id, s.id)
  }

  const menuItems: RMenuItem[] = [
    l.status === 'live'
      ? {
          icon: <CirclePause size={17} />,
          label: t('myListings.pause', 'Pause listing'),
          sub: t('myListings.pauseSub', 'Hidden from search, stats kept'),
          onClick: () => {
            updateMyListing(l.id, { status: 'paused' })
            show({ msg: t('myListings.pausedToast', 'Listing paused'), undo: () => updateMyListing(l.id, { status: 'live' }) })
          },
        }
      : l.status === 'paused'
        ? {
            icon: <CirclePlay size={17} />,
            label: t('myListings.resume', 'Resume listing'),
            onClick: () => {
              updateMyListing(l.id, { status: 'live' })
              show({ msg: t('myListings.backInSearch', 'Back in search results') })
            },
          }
        : null,
    l.status !== 'draft'
      ? {
          icon: <Handshake size={17} />,
          label: l.status === 'rented' ? t('myListings.relist', 'Relist') : t('myListings.markRented', 'Mark as rented'),
          onClick: () => {
            const to: MyListingStatus = l.status === 'rented' ? 'live' : 'rented'
            updateMyListing(l.id, { status: to })
            show({
              msg:
                to === 'rented'
                  ? t('myListings.rentedToast', 'Congrats! 🎉 Marked as rented')
                  : t('myListings.relistedToast', 'Relisted'),
            })
          },
        }
      : null,
    {
      icon: <PenLine size={17} />,
      label: t('myListings.edit', 'Edit listing'),
      sub: t('myListings.editSub', 'Reopens the composer with everything filled in'),
      // TODO(post-mock): no draft prefill yet — the composer opens blank.
      onClick: () => router.push('/post'),
    },
    {
      icon: <Trash2 size={17} />,
      label: t('myListings.delete', 'Delete'),
      onClick: () => {
        // Undo re-inserts this listing at its old position instead of restoring
        // a wholesale snapshot (which would revert unrelated edits).
        const index = useMyListingsStore.getState().listings.findIndex((x) => x.id === l.id)
        deleteMyListing(l.id)
        show({ msg: t('myListings.deletedToast', 'Listing deleted'), undo: () => restoreListing(l, index) })
      },
    },
  ].filter(Boolean) as RMenuItem[]

  return (
    <article
      className={cn(
        rcardHover,
        'flex flex-col gap-3 rounded-shape-lg border border-outline-variant bg-surface p-3.5 shadow-e1',
        (l.status === 'paused' || l.status === 'rented') && 'opacity-[0.92]'
      )}
    >
      <div className="flex items-start gap-3.5">
        <div className="relative shrink-0">
          <PhotoBlock
            palette={l.palette}
            className="rounded-shape-sm"
            style={{ width: 108, height: 82, filter: l.status === 'live' ? 'none' : 'grayscale(0.5)' }}
          />
          {l.fresh ? (
            <span className="absolute left-[6px] top-[6px] rounded-full bg-primary px-[7px] py-[2px] text-[8.5px] font-extrabold tracking-[0.5px] text-white">
              {t('myListings.newBadge', 'NEW')}
            </span>
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cn('inline-flex items-center gap-1 text-[11px] font-bold', st.cls)}>
              <StIcon size={13} /> {statusLabel.toUpperCase()}
            </span>
            {promoted ? (
              <span className="inline-flex items-center gap-[3px] rounded-full bg-primary px-[7px] py-[1.5px] text-[8.5px] font-extrabold tracking-[0.5px] text-white">
                <Rocket size={9} /> {t('myListings.promoBadge', 'PROMOTED')}
              </span>
            ) : null}
            <span className="text-[11px] text-ink-muted">· {l.createdLabel}</span>
          </div>
          <div className="mt-[3px] overflow-hidden text-ellipsis whitespace-nowrap text-[15.5px] font-bold tracking-[-0.2px]">
            {l.title}
          </div>
          <div className="mt-[2px] text-[12px] text-ink-secondary">
            {l.rooms > 0 ? `${l.rooms}+kk · ` : ''}
            {l.area} m² · {l.district}
            <span className="ml-2 font-mono font-bold text-primary">{fmtCZK(l.price)}</span>
            {l.transaction !== 'sale' ? <span className="text-ink-muted">{t('post.perMonth', '/mo')}</span> : null}
          </div>
        </div>
        {l.status !== 'draft' && l.status !== 'rented' ? (
          <IconBtn
            size={34}
            title={
              promoted
                ? t('myListings.promoManage', 'Manage promotion')
                : t('myListings.promoTitle', 'Promote this listing')
            }
            onClick={() => setPromoteOpen(true)}
            className={promoted ? 'bg-primary-container' : 'bg-transparent'}
          >
            <Rocket size={18} className={promoted ? 'text-on-primary-container' : 'text-ink-secondary'} />
          </IconBtn>
        ) : null}
        <IconBtn
          size={34}
          title={t('myListings.viewTitle', 'How seekers see it')}
          onClick={() => setPreviewOpen(true)}
          className="bg-transparent"
        >
          <Eye size={18} className="text-ink-secondary" />
        </IconBtn>
        <RMenu
          align="right"
          width={250}
          items={menuItems}
          button={(open, toggle) => (
            <IconBtn
              size={34}
              title={t('myListings.actions', 'Listing actions')}
              onClick={toggle}
              className={open ? 'bg-surface-high' : 'bg-transparent'}
            >
              <MoreHorizontal size={19} className="text-ink-secondary" />
            </IconBtn>
          )}
        />
        <ListingPreviewModal l={l} open={previewOpen} onClose={() => setPreviewOpen(false)} />
        <PromoteModal l={l} open={promoteOpen} onClose={() => setPromoteOpen(false)} />
      </div>
      {l.status !== 'draft' ? (
        <div className="flex flex-wrap items-center gap-[22px] rounded-shape-sm bg-surface-low px-3 py-[10px]">
          {/* Demand stats are a paid-promotion perk (user decision 2026-07-15). */}
          {promoted ? (
            <MlStat icon={Heart} value={l.saves} label={t('myListings.savesBySeekers', 'saves by seekers')} dim={l.status !== 'live'} />
          ) : null}
          <AskingPriceStat
            l={l}
            label={
              l.transaction === 'sale'
                ? t('myListings.askingPrice', 'asking price')
                : t('myListings.askingPerMonth', 'asking / month')
            }
            dim={l.status !== 'live'}
            open={pricesOpen}
            onToggle={() => setPricesOpen((v) => !v)}
          />
        </div>
      ) : (
        <div className="rounded-shape-sm bg-surface-low px-3 py-[10px]">
          <div className="mb-[6px] flex justify-between text-[11.5px] text-ink-muted">
            <span>{t('myListings.completeness', 'Listing completeness')}</span>
            <span className="font-mono font-bold">60%</span>
          </div>
          <div className="h-1 rounded-[2px] bg-surface-high">
            <div className="h-full w-[60%] rounded-[2px] bg-warning" />
          </div>
        </div>
      )}
      {pricesOpen && l.status !== 'draft' ? <PriceEditor l={l} onClose={() => setPricesOpen(false)} /> : null}
      {l.status !== 'draft' ? <LiveUpdates l={l} onChange={(ups) => updateMyListing(l.id, { updates: ups })} /> : null}
      {(l.suggestions || []).map((s) => (
        <SuggestionRow key={s.id} s={s} onAction={() => applySuggestion(s)} onDismiss={() => dismissSuggestion(l.id, s.id)} />
      ))}
    </article>
  )
}
